import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTag } from '@fortawesome/pro-regular-svg-icons';

import { faGift } from '@fortawesome/pro-light-svg-icons';
import { createStyles } from 'utils/createStyle';
import { requestCartItems, resetCart, updateCart } from 'common/features/store/duck/cart/duck';
import { removeItemFromCart } from 'common/api/e-comm/cart/lineitem';
import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { monetizeWithDecimal } from 'common/utils/strings';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { getWalletCardDisplayName } from 'features/order-summary/utils';
import {
    getProductTypeIcon,
    getProductTypeDisplayName,
} from 'features/order-summary/components/summary/OrderSummaryGrid';
import { FeatureFlag } from 'components/feature-flag';
import { paymentWebsiteOrigin } from 'utils';

const styles = createStyles({
    listItemContent: [
        'p-2 grid gap-2 w-full',
        {
            gridTemplateColumns: 'max-content auto max-content',
        },
    ],
});

export interface CartLineItemsProps {
    lineItems: CartLineItem[];
    loading: boolean;
}

export const CartLineItems = ({ lineItems, loading }: CartLineItemsProps) => {
    const dispatch = useDispatch<any>();
    const itemsInCart = lineItems.length > 0;
    const walletCards = useCardsFromWallet();

    const onRemoveItem = async (lineItem: CartLineItem) => {
        (async () => {
            const response = await removeItemFromCart(lineItem.id);
            if (response.response && response.response.ok) {
                let iframe = document.getElementById('payment-iframe') as any; // Get iframe by ID
                iframe = iframe && iframe.contentWindow; // Get iframe window

                if (response.data) {
                    dispatch(updateCart({ cart: response.data }));
                    if (iframe) {
                        iframe.postMessage(
                            { type: 'cartUpdated', cart: response.data },
                            paymentWebsiteOrigin()
                        );
                    }
                } else {
                    dispatch(resetCart());
                    if (iframe) {
                        iframe.postMessage({ type: 'resetCart' }, paymentWebsiteOrigin());
                    }
                }
            }
            await requestCartItems()(dispatch);
        })();
    };

    if (loading && !itemsInCart) {
        return (
            <div>
                <ul>
                    <li className="mb-2">
                        <Skeleton className="w-full h-20" />
                    </li>
                    <li className="mb-2">
                        <Skeleton className="w-full h-20" />
                    </li>
                    <li className="mb-2">
                        <Skeleton className="w-full h-20" />
                    </li>
                </ul>
            </div>
        );
    }

    return (
        <div>
            <ul>
                {lineItems.map((lineItem) => {
                    return (
                        <li key={lineItem.id} className="mb-2">
                            <div className="relative">
                                <div
                                    className={styles.listItemContent}
                                    style={{ visibility: loading ? 'hidden' : 'visible' }}
                                >
                                    <div className="flex w-12 items-center justify-center">
                                        {lineItem.brand && (
                                            <img src={lineItem.brand.imageUrl} className="w-full" />
                                        )}
                                        {lineItem.product.isLocalProduct && (
                                            <FontAwesomeIcon
                                                className={'text-brand'}
                                                icon={faTag}
                                                size="lg"
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center justify-left truncate">
                                        <div className="block h-full w-full">
                                            <div className="flex">
                                                <div className=" w-full text-left text-xs align-middle text-brand-dark font-bold">
                                                    <span
                                                        className="inline-block align-middle truncate w-full"
                                                        style={{
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '105px',
                                                            minWidth: '100%',
                                                        }}
                                                    >
                                                        {lineItem.product.isLocalProduct
                                                            ? lineItem.product.name
                                                            : truncateName(
                                                                  lineItem.brand?.name || ''
                                                              )}
                                                        <FeatureFlag flagKey="enable_custom_card_names">
                                                            {getWalletCardDisplayName(
                                                                lineItem,
                                                                walletCards
                                                            )
                                                                ? ` - ${getWalletCardDisplayName(
                                                                      lineItem,
                                                                      walletCards
                                                                  )}`
                                                                : ''}
                                                        </FeatureFlag>
                                                    </span>
                                                </div>
                                                <div className=" w-auto text-right text-xs align-middle text-brand-dark font-bold">
                                                    <span className="inline-block align-middle truncate w-auto">
                                                        {monetizeWithDecimal(lineItem.subtotal)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="text-xs text-left font-bold">
                                                <div className="text-grey-1">
                                                    <div className="inline-flex items-center">
                                                        {lineItem.giftDetail && (
                                                            <FontAwesomeIcon
                                                                icon={faGift}
                                                                style={{ fontSize: '12px' }}
                                                                className="mr-1"
                                                            />
                                                        )}
                                                        <span className="inline-flex items-center mr-1">
                                                            {getProductTypeIcon(
                                                                lineItem.product,
                                                                '0.875rem'
                                                            )}
                                                        </span>
                                                        {getProductTypeDisplayName(
                                                            lineItem.product
                                                        )}{' '}
                                                        -{' '}
                                                        <span>
                                                            {monetizeWithDecimal(lineItem.price)}
                                                        </span>{' '}
                                                        (x{lineItem.quantity})
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start justify-end">
                                        <button
                                            className=" text-sm text-grey-2 font-bold cursor-pointer"
                                            style={{ marginTop: '-0.5px' }}
                                            onClick={() => onRemoveItem(lineItem)}
                                        >
                                            <FontAwesomeIcon icon={faTimes} size="1x" />
                                        </button>
                                    </div>
                                </div>
                                {loading && <Skeleton className="absolute inset-0" />}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const truncateName = (str: string, cutoff = 15): string => {
    if (str.length <= cutoff) {
        return str;
    }
    return str.substring(0, cutoff) + '...';
};
